import React, { ReactNode } from "react"
import { StatsigProvider, useClientAsyncInit } from "@statsig/react-bindings"
import { StatsigAutoCapturePlugin } from "@statsig/web-analytics"
import { StatsigSessionReplayPlugin } from "@statsig/session-replay"
import { useConsentGranted } from "./consent/useConsent"

// Generate a cryptographically secure random string
const generateSecureRandomId = (): string => {
  const array = new Uint8Array(16)
  crypto.getRandomValues(array)
  return Array.from(array, (byte) => byte.toString(16).padStart(2, "0")).join("")
}

// Generate a stable user ID (anonymous or from localStorage). Only called
// after analytics consent has been granted, so this is not a pre-consent write.
const getUserID = () => {
  if (typeof window === "undefined") return "ssr-user"

  let userID = localStorage.getItem("statsig_user_id")
  if (!userID) {
    userID = `anon-${generateSecureRandomId()}`
    localStorage.setItem("statsig_user_id", userID)
  }
  return userID
}

interface StatsigProviderWrapperProps {
  children: ReactNode
}

const StatsigProviderWrapper: React.FC<StatsigProviderWrapperProps> = ({ children }) => {
  const statsigClientKey = process.env.GATSBY_STATSIG_CLIENT_KEY
  const granted = useConsentGranted()

  // Gate Statsig behind analytics consent. Session Replay is gated additionally
  // on marketing consent (it is literal session recording).
  if (!granted.analytics) {
    return <>{children}</>
  }

  if (!statsigClientKey || statsigClientKey === "YOUR_STATSIG_CLIENT_KEY_HERE") {
    console.warn("Statsig client key not configured. Skipping Statsig initialization.")
    return <>{children}</>
  }

  return (
    <StatsigInitializer statsigClientKey={statsigClientKey} enableSessionReplay={granted.marketing}>
      {children}
    </StatsigInitializer>
  )
}

const StatsigInitializer: React.FC<{
  statsigClientKey: string
  enableSessionReplay: boolean
  children: ReactNode
}> = ({ statsigClientKey, enableSessionReplay, children }) => {
  const plugins = enableSessionReplay
    ? [new StatsigAutoCapturePlugin(), new StatsigSessionReplayPlugin()]
    : [new StatsigAutoCapturePlugin()]

  const { client } = useClientAsyncInit(
    statsigClientKey,
    { userID: getUserID() },
    {
      environment: {
        tier: process.env.NODE_ENV || "development",
      },
      plugins,
    }
  )

  return <StatsigProvider client={client}>{children}</StatsigProvider>
}

// Export wrapRootElement for gatsby-browser.js. Note: this must be wrapped
// *inside* the ConsentProvider so that useConsentGranted has a context to read.
export const wrapRootElement = ({ element }: { element: ReactNode }) => {
  return <StatsigProviderWrapper>{element}</StatsigProviderWrapper>
}
